import { useState, useEffect } from 'react';
import { Users, Plus, X, Mail, Shield, User, AlertCircle, Check, Loader2 } from '../../icons/wp-icons';
import { createBenutzer } from '../../api/benutzerApi';

const ROLLEN = [
  { key: 'admin', label: 'Administrator', description: 'Voller Zugriff auf alle Funktionen' },
  { key: 'mitarbeiter', label: 'Mitarbeiter', description: 'Eingeschränkter Zugriff, kann Termine und Kunden verwalten' }
];

function TeamStep({ data = {}, onDataChange, user }) {
  const [einladungen, setEinladungen] = useState([]);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRolle, setNewRolle] = useState('mitarbeiter');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Immer valid, da optional
    onDataChange({}, true);
  }, []);

  const addEinladung = () => {
    if (!newName.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }

    if (!newEmail.trim()) {
      setError('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
      return;
    }

    if (newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      setError('Sie können sich nicht selbst einladen');
      return;
    }

    if (einladungen.some(e => e.email.toLowerCase() === newEmail.toLowerCase())) {
      setError('Diese E-Mail-Adresse wurde bereits hinzugefügt');
      return;
    }

    setEinladungen([...einladungen, { name: newName.trim(), email: newEmail.trim(), rolle: newRolle, status: 'pending' }]);
    setNewName('');
    setNewEmail('');
    setError('');
  };

  const removeEinladung = (index) => {
    setEinladungen(einladungen.filter((_, i) => i !== index));
  };

  const sendEinladungen = async () => {
    if (einladungen.length === 0) return;

    setSending(true);
    setError('');
    setSuccess('');

    let successCount = 0;
    let errorMessages = [];
    const updated = [...einladungen];

    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === 'pending') {
        try {
          await createBenutzer({
            name: updated[i].name,
            email: updated[i].email,
            rolle: updated[i].rolle
          });
          updated[i] = { ...updated[i], status: 'sent' };
          successCount++;
        } catch (err) {
          const msg = err.response?.data?.error || 'Unbekannter Fehler';
          updated[i] = { ...updated[i], status: 'error', error: msg };
          errorMessages.push(`${updated[i].email}: ${msg}`);
        }
      }
    }

    setEinladungen(updated);

    if (successCount > 0) {
      setSuccess(`${successCount} Einladung${successCount > 1 ? 'en' : ''} erfolgreich versendet!`);
    }
    if (errorMessages.length > 0) {
      setError(errorMessages.join('\n'));
    }

    setSending(false);
  };

  const pendingCount = einladungen.filter(e => e.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Team einladen
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Laden Sie Mitarbeiter ein, die mit WerkPilot arbeiten sollen.
        </p>
      </div>

      {/* Aktueller Benutzer */}
      <div className="bg-gray-50 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#378ab2] rounded-full flex items-center justify-center text-white font-medium">
            {user?.name?.[0] || 'U'}
          </div>
          <div>
            <div className="font-medium text-gray-900">{user?.name}</div>
            <div className="text-sm text-gray-500">{user?.email}</div>
          </div>
          <span className="ml-auto px-3 py-1 bg-[#378ab2]/10 text-[#378ab2] text-sm rounded-full font-medium">
            Inhaber
          </span>
        </div>
      </div>

      {/* Neue Einladung */}
      <div className="border rounded-xl p-4">
        <h4 className="font-medium text-gray-900 mb-4">Mitarbeiter einladen</h4>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1">
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                  placeholder="Name des Mitarbeiters"
                />
              </div>
            </div>

            <div className="flex-1">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addEinladung()}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                  placeholder="mitarbeiter@firma.de"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-3">
            <select
              value={newRolle}
              onChange={(e) => setNewRolle(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
            >
              {ROLLEN.map((rolle) => (
                <option key={rolle.key} value={rolle.key}>
                  {rolle.label} – {rolle.description}
                </option>
              ))}
            </select>

            <button
              onClick={addEinladung}
              className="px-4 py-2 bg-[#378ab2] text-white rounded-lg hover:bg-[#2a6d8a] transition flex items-center gap-2 justify-center"
            >
              <Plus className="w-5 h-5" />
              Hinzufügen
            </button>
          </div>
        </div>

        {error && (
          <p className="text-red-600 text-sm mt-2 whitespace-pre-line">{error}</p>
        )}
      </div>

      {/* Einladungsliste */}
      {einladungen.length > 0 && (
        <div className="border rounded-xl divide-y">
          {einladungen.map((einladung, index) => (
            <div key={index} className="flex items-center gap-3 p-4">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-gray-400" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-gray-900">{einladung.name}</div>
                <div className="text-sm text-gray-500">
                  {einladung.email} · {ROLLEN.find(r => r.key === einladung.rolle)?.label}
                </div>
              </div>
              {einladung.status === 'sent' ? (
                <span className="flex items-center gap-1 text-green-600 text-sm">
                  <Check className="w-4 h-4" />
                  Gesendet
                </span>
              ) : einladung.status === 'error' ? (
                <span className="flex items-center gap-1 text-red-600 text-sm" title={einladung.error}>
                  <AlertCircle className="w-4 h-4" />
                  Fehler
                </span>
              ) : (
                <button
                  onClick={() => removeEinladung(index)}
                  className="text-gray-400 hover:text-red-500 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}

          {pendingCount > 0 && (
            <div className="p-4 bg-gray-50">
              <button
                onClick={sendEinladungen}
                disabled={sending}
                className="w-full py-2 bg-[#378ab2] text-white rounded-lg hover:bg-[#2a6d8a] transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Sende Einladungen...
                  </>
                ) : (
                  <>
                    <Mail className="w-5 h-5" />
                    {pendingCount} Einladung{pendingCount > 1 ? 'en' : ''} senden
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
          <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
          <span className="text-green-800">{success}</span>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Optional:</strong> Sie können jederzeit weitere Teammitglieder über die Mitarbeiterverwaltung einladen.
          Die eingeladenen Mitarbeiter erhalten eine E-Mail mit einem Link zum Passwort festlegen.
        </div>
      </div>
    </div>
  );
}

export default TeamStep;
